'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { type Address, type Hex, parseAbiItem } from 'viem';
import { getLogs } from 'viem/actions';
import { useClient, useConfig } from 'wagmi';
import { getPublicClient } from 'wagmi/actions';
import type { ChainId } from '../configs/chains';
import { useUserAccount } from '../providers/UserAccountProvider';
import { maybeGetAddress } from '../utils/account.utils';
import { fetchSafeWalletHealth, type SafeWalletHealthWarning } from '../utils/safe-wallet.utils';
import { useWalletConnectMetadata } from './useWalletConnectMetadata';

// Returns true when using a Safe Smart account:
// - app running as a Safe App
// - user connected via WalletConnect to a Safe Account
export function useIsSafeAccount(): boolean {
  const isSafeApp = useIsSafeApp();
  const {
    isSafeAccountViaWalletConnect
  } = useWalletConnectMetadata();
  return isSafeApp || isSafeAccountViaWalletConnect;
}

// Returns true if the user is connected with a Safe Account
function useIsSafeApp(): boolean {
  const {
    connector
  } = useUserAccount();
  return connector?.id === 'safe';
}
export const DEFAULT_TX_TIMEOUT = 120_000;
export const SAFE_WALLET_TX_TIMEOUT = 600_000;
export const SAFE_WALLET_TX_TIMEOUT_MINUTES = Math.round(SAFE_WALLET_TX_TIMEOUT / 60_000);
export function useSafeTxHash({
  wagmiTxHash,
  chainId,
  blockNumber,
  enabled
}: {
  wagmiTxHash: Hex | undefined;
  chainId: number;
  blockNumber?: bigint;
  enabled: boolean;
}) {
  const [safeTxHash, setSafeTxHash] = useState<Hex | undefined>();
  const {
    userAddress
  } = useUserAccount();
  const viemClient = useClient({
    chainId
  });
  const queryFn = useCallback(() => viemClient ? getLogs(viemClient, {
    event: parseAbiItem(['event ExecutionSuccess(bytes32 txHash, uint256 payment)']),
    address: userAddress,
    fromBlock: blockNumber
  }) : [], [viemClient, userAddress, blockNumber]);

  // Get stable reference to `select`, otherwise Tanstack re-renders regardless
  const select = useCallback((data: Awaited<ReturnType<typeof queryFn>>) => data.find(log => log.args.txHash === wagmiTxHash), [wagmiTxHash]);
  const safeLogs = useQuery({
    queryKey: ['safeLogs', userAddress, wagmiTxHash],
    queryFn,
    select,
    refetchInterval: safeTxHash ? 0 : 2000,
    // Refetch every 2 seconds until the safeTxHash is found
    enabled: enabled && !!wagmiTxHash && !!userAddress && !!viemClient
  });

  // A new wagmi hash means a new transaction: drop the previously resolved
  // on-chain hash so consumers never observe the old receipt as confirmed
  // while the next Safe transaction is still resolving.
  useEffect(() => {
    setSafeTxHash(undefined);
  }, [wagmiTxHash]);
  useEffect(() => {
    const txHash = safeLogs.data?.transactionHash;
    if (txHash) {
      setSafeTxHash(txHash);
    }
  }, [safeLogs.data?.transactionHash, safeTxHash]);
  return {
    safeTxHash
  };
}
type UseSafeWalletHealthParams = {
  address?: Address;
  chainId: ChainId;
  overlapAddress?: Address;
};
type UseSafeWalletHealthResponse = {
  isSafeWallet: boolean | null;
  isConfirmedEoa: boolean;
  threshold?: number;
  owners?: readonly Address[];
  healthWarnings: SafeWalletHealthWarning[];
  isLoading: boolean;
  error: Error | null;
};

/** @public */
export function useSafeWalletHealth(t0) {
  "use memo";

  const $ = _c(28);
  const {
    address,
    chainId,
    overlapAddress
  } = t0;
  const config = useConfig();
  let t1;
  if ($[0] !== address) {
    t1 = maybeGetAddress(address);
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedAddress = t1;
  let t2;
  if ($[2] !== overlapAddress) {
    t2 = maybeGetAddress(overlapAddress);
    $[2] = overlapAddress;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const normalizedOverlapAddress = t2;
  let t3;
  if ($[4] !== chainId || $[5] !== normalizedAddress || $[6] !== normalizedOverlapAddress) {
    t3 = ["safeWalletHealth", chainId, normalizedAddress, normalizedOverlapAddress];
    $[4] = chainId;
    $[5] = normalizedAddress;
    $[6] = normalizedOverlapAddress;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== chainId || $[9] !== config || $[10] !== normalizedAddress || $[11] !== normalizedOverlapAddress) {
    t4 = () => {
      const client = getPublicClient(config, {
        chainId
      });
      if (!client || !normalizedAddress) {
        throw new Error("Safe wallet health query is disabled");
      }
      return fetchSafeWalletHealth(client, normalizedAddress, normalizedOverlapAddress);
    };
    $[8] = chainId;
    $[9] = config;
    $[10] = normalizedAddress;
    $[11] = normalizedOverlapAddress;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = !!address;
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const query = useQuery(t6);
  if (query.isLoading || !address) {
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = [];
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== query.isLoading) {
      t8 = {
        isSafeWallet: null,
        isConfirmedEoa: false,
        healthWarnings: t7,
        isLoading: query.isLoading,
        error: null
      };
      $[18] = query.isLoading;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    return t8;
  }
  if (query.error || !query.data) {
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = [];
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== query.error) {
      t8 = {
        isSafeWallet: null,
        isConfirmedEoa: false,
        healthWarnings: t7,
        isLoading: false,
        error: query.error
      };
      $[21] = query.error;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    return t8;
  }
  if (!query.data.isSafeWallet) {
    let t7;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        isSafeWallet: false,
        isConfirmedEoa: true,
        healthWarnings: [],
        isLoading: false,
        error: null
      };
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    return t7;
  }
  let t7;
  if ($[24] !== query.data.healthWarnings || $[25] !== query.data.owners || $[26] !== query.data.threshold) {
    t7 = {
      isSafeWallet: true,
      isConfirmedEoa: false,
      threshold: query.data.threshold,
      owners: query.data.owners,
      healthWarnings: query.data.healthWarnings,
      isLoading: false,
      error: null
    };
    $[24] = query.data.healthWarnings;
    $[25] = query.data.owners;
    $[26] = query.data.threshold;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}